import classNames from "classnames";
import Popover from "components/Popover";
import { ConsensusMeterCategory } from "helpers/api";
import pluralize from "helpers/pluralize";
import useLabels from "hooks/useLabels";
import { MeterCategories } from "../types";
import { getCategoriesToColors } from "../utils";

interface MeterBarProps {
  category: MeterCategories;
  categoryData: ConsensusMeterCategory | null;
  numResultsAnalyzed: number;
  isLoading: boolean;
  hasMeterFailed: boolean;
  onTooltipShown: () => void;
  onTooltipHidden: () => void;
  faded?: boolean;
}

const DEFAULT_PAPER_PERCENTAGE = 25;

export const MeterBar = ({
  category,
  categoryData,
  numResultsAnalyzed,
  isLoading,
  hasMeterFailed,
  onTooltipShown,
  onTooltipHidden,
  faded,
}: MeterBarProps) => {
  const [tooltipLabels] = useLabels("consensus-meter.meter-bar-tooltips");

  const paperCount = categoryData ? categoryData.paper_count : 0;

  const paperPercentage =
    !hasMeterFailed && numResultsAnalyzed > 0
      ? (100 * paperCount) / numResultsAnalyzed
      : DEFAULT_PAPER_PERCENTAGE;

  const tooltipContent = pluralize(tooltipLabels[category], {
    PAPER_PERCENTAGE: Math.round(paperPercentage).toString(),
  });

  return (
    <div
      style={{
        width: paperPercentage ? `${paperPercentage}%` : "8px",
      }}
      className="first:rounded-l last:rounded-r transition-[width] duration-500"
      data-testid={`meter-bar-${category}${isLoading ? "-loading" : ""}`}
    >
      <Popover
        tooltipContent={
          <p className="tiny-normal text-center max-w-[234px]">
            {tooltipContent}
          </p>
        }
        widthFull
        childrenClassName="rounded-[inherit]"
        placement="top"
        theme="dark"
        onShow={onTooltipShown}
        onHide={onTooltipHidden}
        size="small"
        disabled={isLoading || hasMeterFailed}
      >
        <div
          style={
            {
              "--base-background": `${getCategoriesToColors()[category]}`,
            } as React.CSSProperties & { [key: string]: string }
          }
          className={classNames(
            "h-5 flex items-center justify-end pr-1 w-full rounded-[inherit] transition-all",
            {
              "vertical-dark-gradient": numResultsAnalyzed > 0,
              "animate-pulse bg-border-base": !(numResultsAnalyzed > 0),
              "opacity-50":
                faded || (paperCount === 0 && numResultsAnalyzed > 0),
              "min-w-4": paperCount > 0,
            }
          )}
        >
          {paperCount !== 0 && (
            <div className="text-white tiny-mono-semibold">{paperCount}</div>
          )}
        </div>
      </Popover>
    </div>
  );
};
